import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { Kitchen } from '@rta/rta-blueprint-library';

/** A free-standing rectangle drawn on a blank sheet — deliberately NOT a domain Panel, so it never reaches pricing or geometry. */
export type CustomPanelData = {
	id: string;
	width: number;
	height: number;
};

export type CustomBlueprintPage = {
	id: string;
	panels: Array<CustomPanelData>;
};

export const CUSTOM_PANEL_DEFAULT_WIDTH = 25;
export const CUSTOM_PANEL_DEFAULT_HEIGHT = 34.5;

const CUSTOM_PAGE_IDS_KEY = 'customPages.ids';
const CUSTOM_PAGE_KEY_PREFIX = 'customPage.';
const CUSTOM_PAGE_KEY_SUFFIX = '.panels';

const getPanelsKey = (pageId: string): string => `${CUSTOM_PAGE_KEY_PREFIX}${pageId}${CUSTOM_PAGE_KEY_SUFFIX}`;

const parseArray = <T,>(raw: string | undefined): Array<T> => {
	if (!raw) return [];
	try {
		const parsed = JSON.parse(raw) as Array<T>;
		return Array.isArray(parsed) ? parsed : [];
	} catch (_error) {
		return [];
	}
};

/** Reads the blank sheets back out of the kitchen's blueprint overrides — the same store every other blueprint-view-only edit uses. */
export const readCustomPages = (kitchen: Kitchen | null): Array<CustomBlueprintPage> => {
	const overrides = kitchen?.blueprintOverrides ?? {};
	return parseArray<string>(overrides[CUSTOM_PAGE_IDS_KEY])
		.map((id) => ({ id, panels: parseArray<CustomPanelData>(overrides[getPanelsKey(id)]) }));
};

export type BlueprintCustomPagesContextValue = {
	customPages: Array<CustomBlueprintPage>;
	addCustomPage: () => void;
	removeCustomPage: (pageId: string) => void;
	addCustomPanel: (pageId: string) => void;
	setCustomPanelSize: (pageId: string, panelId: string, size: { width: number; height: number }) => void;
	removeCustomPanel: (pageId: string, panelId: string) => void;
};

const EMPTY_VALUE: BlueprintCustomPagesContextValue = {
	customPages: [],
	addCustomPage: () => {},
	removeCustomPage: () => {},
	addCustomPanel: () => {},
	setCustomPanelSize: () => {},
	removeCustomPanel: () => {}
};

const BlueprintCustomPagesContext = createContext<BlueprintCustomPagesContextValue | null>(null);

export type BlueprintCustomPagesProviderProps = {
	kitchen: Kitchen | null;
	children: React.ReactNode;
};

/**
 * Owns the blank "CUSTOM PANEL" sheets a user appends to the blueprint. Local state drives the UI
 * and the kitchen overrides are write-through only — blueprint-view never subscribes to
 * ('kitchen','updated'), the same reason BlueprintPageFontControl mirrors its value in state.
 */
export const BlueprintCustomPagesProvider = (props: BlueprintCustomPagesProviderProps): React.ReactElement => {
	const { kitchen, children } = props;

	const [customPages, setCustomPages] = useState<Array<CustomBlueprintPage>>(() => readCustomPages(kitchen));

	// The kitchen arrives after the first render (and is replaced outright on a version swap), so re-read rather than keeping whatever the initializer saw.
	useEffect(() => { setCustomPages(readCustomPages(kitchen)); }, [kitchen]);

	// Write-through: state is the source of truth and the overrides mirror it, so every mutator below
	// stays a pure functional update (two clicks in one tick both land). Unchanged keys are skipped so
	// re-reading a design never marks it dirty on its own.
	useEffect(() => {
		if (!kitchen) return;

		const ids = JSON.stringify(customPages.map((page) => page.id));
		if ((kitchen.blueprintOverrides[CUSTOM_PAGE_IDS_KEY] ?? '[]') !== ids) kitchen.setBlueprintOverride(CUSTOM_PAGE_IDS_KEY, ids);

		customPages.forEach((page) => {
			const panels = JSON.stringify(page.panels);
			if (kitchen.blueprintOverrides[getPanelsKey(page.id)] !== panels) kitchen.setBlueprintOverride(getPanelsKey(page.id), panels);
		});

		Object.keys(kitchen.blueprintOverrides)
			.filter((key) => key.startsWith(CUSTOM_PAGE_KEY_PREFIX) && key.endsWith(CUSTOM_PAGE_KEY_SUFFIX))
			.filter((key) => !customPages.some((page) => getPanelsKey(page.id) === key))
			.forEach((key) => kitchen.clearBlueprintOverride(key));
	}, [kitchen, customPages]);

	const updatePanels = useCallback((pageId: string, update: (panels: Array<CustomPanelData>) => Array<CustomPanelData>): void => {
		setCustomPages((pages) => pages.map((page) => page.id === pageId ? { ...page, panels: update(page.panels) } : page));
	}, []);

	const addCustomPage = useCallback((): void => {
		setCustomPages((pages) => [...pages, { id: crypto.randomUUID(), panels: [] }]);
	}, []);

	const removeCustomPage = useCallback((pageId: string): void => {
		setCustomPages((pages) => pages.filter((page) => page.id !== pageId));
	}, []);

	const addCustomPanel = useCallback((pageId: string): void => {
		updatePanels(pageId, (panels) => [...panels, {
			id: crypto.randomUUID(),
			width: CUSTOM_PANEL_DEFAULT_WIDTH,
			height: CUSTOM_PANEL_DEFAULT_HEIGHT
		}]);
	}, [updatePanels]);

	const setCustomPanelSize = useCallback((pageId: string, panelId: string, size: { width: number; height: number }): void => {
		updatePanels(pageId, (panels) => panels.map((panel) => panel.id === panelId ? { ...panel, ...size } : panel));
	}, [updatePanels]);

	const removeCustomPanel = useCallback((pageId: string, panelId: string): void => {
		updatePanels(pageId, (panels) => panels.filter((panel) => panel.id !== panelId));
	}, [updatePanels]);

	const value = useMemo((): BlueprintCustomPagesContextValue => ({
		customPages,
		addCustomPage,
		removeCustomPage,
		addCustomPanel,
		setCustomPanelSize,
		removeCustomPanel
	}), [customPages, addCustomPage, removeCustomPage, addCustomPanel, setCustomPanelSize, removeCustomPanel]);

	return (
		<BlueprintCustomPagesContext.Provider value={value}>
			{children}
		</BlueprintCustomPagesContext.Provider>
	);
};

export const useBlueprintCustomPages = (): BlueprintCustomPagesContextValue => {
	return useContext(BlueprintCustomPagesContext) ?? EMPTY_VALUE;
};
